import React, { useState, useEffect, useMemo } from 'react';
import { Provider, UserRole, Client } from '../types';
import { ProviderDetailModal } from './ProviderDetailModal';
import { AddAppUserModal } from './AddAppUserModal';
import { LinkProviderModal } from './LinkProviderModal';
import { ArcSearchInput } from './ArcSearchInput';
import {
  UserCheck,
  Users,
  Plus,
  Search,
  CheckCircle2,
  AlertCircle,
  X,
  Edit2,
  Trash2,
  ArrowRightLeft,
  ShieldCheck,
  FileText,
  FileCheck2,
  RefreshCw,
  SlidersHorizontal,
  Mail,
  Phone,
  Calendar,
  Check,
  Sparkles,
  Shield,
  Crown,
  ChevronDown,
  ChevronRight,
  Activity,
  UserPlus,
  Link2,
  Unlink
} from 'lucide-react';
import { PulseCoreIcon } from './PulseCoreIcon';
import { getRoleEntitlementStyles } from '../utils/roleAccent';
import { ProviderActivityView } from './ProviderActivityView';

interface ProviderManagementViewProps {
  userRole: UserRole;
  onNavigateToTab?: (tab: string, subTab?: string) => void;
  onSelectClient?: (id: string) => void;
  initialSubView?: 'ROSTER' | 'APP_USERS' | 'ACTIVITY';
  embedded?: boolean;
}

export const ProviderManagementView: React.FC<ProviderManagementViewProps> = ({
  userRole,
  onNavigateToTab,
  onSelectClient,
  initialSubView,
  embedded = false
}) => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [pendingSupervisionCount, setPendingSupervisionCount] = useState<number>(0);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'ALL' | 'SLP' | 'SLPA' | 'SUPERVISOR'>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ACTIVE');

  // Modals
  const [selectedProviderIdForProfile, setSelectedProviderIdForProfile] = useState<string | null>(null);
  const [selectedProviderForCaseload, setSelectedProviderForCaseload] = useState<Provider | null>(null);
  const [providerToEdit, setProviderToEdit] = useState<Provider | null>(null);
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);

  // Status toggle confirmation
  const [statusToggleConfirm, setStatusToggleConfirm] = useState<{ provider: Provider; nextActive: boolean } | null>(null);

  const isOwner = userRole === 'OWNER' || userRole === 'ADMIN';
  const isAdmin = isOwner || userRole === 'OFFICE' || userRole === 'REFERRAL_COORDINATOR';
  const isSupervisor = userRole === 'SUPERVISOR';
  const isReferralCoordinator = userRole === 'REFERRAL_COORDINATOR';

  // Subview toggle: Roster vs. App User Role Entitlements vs. Provider Activity
  const [activeSubView, setActiveSubView] = useState<'ROSTER' | 'APP_USERS' | 'ACTIVITY'>(initialSubView || 'ROSTER');

  useEffect(() => {
    if (initialSubView) {
      setActiveSubView(initialSubView);
    }
  }, [initialSubView]);
  const [mobileAppUsersExpanded, setMobileAppUsersExpanded] = useState(false);
  const [appUsers, setAppUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [linkModalUser, setLinkModalUser] = useState<{ user: any; currentProvider: Provider | null } | null>(null);
  const [savingStatusUserId, setSavingStatusUserId] = useState<string | null>(null);
  const [currentAuthUser, setCurrentAuthUser] = useState<any>(null);

  useEffect(() => {
    fetch('/api/auth/me')
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (d?.user) setCurrentAuthUser(d.user);
      })
      .catch(() => {});
  }, []);

  const [savingUserRolesId, setSavingUserRolesId] = useState<string | null>(null);
  const [userRolesSuccess, setUserRolesSuccess] = useState<Record<string, string>>({});
  const [userRolesError, setUserRolesError] = useState<string | null>(null);

  // Fetch Providers
  const fetchProviders = async () => {
    try {
      setRefreshing(true);
      const res = await fetch('/api/providers?includeInactive=1');
      if (res.ok) {
        const json = await res.json();
        setProviders(json);
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  // Fetch App Users for Role Entitlements
  const fetchAppUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await fetch('/api/users?includeInactive=1');
      if (res.ok) {
        const json = await res.json();
        setAppUsers(json);
      }
    } catch (err) {
      console.error('Error fetching app users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleToggleUserActive = async (targetUser: any) => {
    if (!isOwner) return;
    const willDeactivate = targetUser.active !== 0;

    if (willDeactivate) {
      const confirmed = window.confirm(
        `Are you sure you want to deactivate application access for ${targetUser.name} (${targetUser.email})?\n\nThey will be immediately blocked from signing into Pulse. Historical records, provider profiles, and documentation will remain preserved.`
      );
      if (!confirmed) return;
    }

    try {
      setSavingStatusUserId(targetUser.id);
      const res = await fetch(`/api/users/${targetUser.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !willDeactivate })
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || 'Failed to update user status');
      }
      const data = await res.json();
      setAppUsers((prev) =>
        prev.map((u) => (u.id === targetUser.id ? { ...u, active: data.user.active } : u))
      );
      setUserRolesSuccess((prev) => ({
        ...prev,
        [targetUser.id]: willDeactivate ? 'Access deactivated' : 'Access reactivated'
      }));
      setTimeout(() => {
        setUserRolesSuccess((prev) => {
          const next = { ...prev };
          delete next[targetUser.id];
          return next;
        });
      }, 2500);
    } catch (err: any) {
      setUserRolesError(err.message || 'Error updating user status');
      setTimeout(() => setUserRolesError(null), 4000);
    } finally {
      setSavingStatusUserId(null);
    }
  };

  const handleToggleUserRole = async (targetUser: any, roleToToggle: string) => {
    // If not owner, only supervisor/assistant can be toggled
    if (!isOwner && (roleToToggle === 'OWNER' || roleToToggle === 'REFERRAL_COORDINATOR')) {
      setUserRolesError('Referral Coordinators may only manage therapist clinical roles (SUPERVISOR, ASSISTANT).');
      setTimeout(() => setUserRolesError(null), 3000);
      return;
    }

    const currentRoles: string[] = targetUser.assignedRoles && targetUser.assignedRoles.length > 0
      ? [...targetUser.assignedRoles]
      : [targetUser.role];

    let newRoles: string[];
    if (currentRoles.includes(roleToToggle)) {
      if (currentRoles.length === 1) {
        setUserRolesError('App user must have at least one assigned application role.');
        setTimeout(() => setUserRolesError(null), 3000);
        return;
      }
      newRoles = currentRoles.filter((r) => r !== roleToToggle);
    } else {
      newRoles = [...currentRoles, roleToToggle];
    }

    try {
      setSavingUserRolesId(targetUser.id);
      setUserRolesError(null);
      const res = await fetch(`/api/users/${targetUser.id}/roles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignedRoles: newRoles })
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || 'Failed to update user roles');
      }
      const data = await res.json();
      setAppUsers((prev) =>
        prev.map((u) => (u.id === targetUser.id ? { ...u, assignedRoles: data.user.assignedRoles, role: data.user.role } : u))
      );
      setUserRolesSuccess((prev) => ({ ...prev, [targetUser.id]: 'Roles updated' }));
      setTimeout(() => {
        setUserRolesSuccess((prev) => {
          const next = { ...prev };
          delete next[targetUser.id];
          return next;
        });
      }, 2500);
    } catch (err: any) {
      setUserRolesError(err.message || 'Error updating roles');
      setTimeout(() => setUserRolesError(null), 4000);
    } finally {
      setSavingUserRolesId(null);
    }
  };

  // Fetch Clients for caseload assignment
  const fetchClients = async () => {
    try {
      const res = await fetch('/api/clients');
      if (res.ok) {
        const json = await res.json();
        setClients(json);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  // Fetch pending supervision count for queue badge (actionable PENDING items only)
  const fetchPendingSupervisionCount = async () => {
    try {
      const res = await fetch('/api/clinical/docs?pending_supervision=true');
      if (res.ok) {
        const docs = await res.json();
        // The main Review Queue badge/count reflects actionable PENDING items, not returned items waiting on clinician
        const pendingCount = Array.isArray(docs)
          ? docs.filter((d: any) => d.supervisor_approval_status !== 'RETURNED').length
          : 0;
        setPendingSupervisionCount(pendingCount);
      }
    } catch (err) {
      console.error('Error fetching pending supervision count:', err);
    }
  };

  useEffect(() => {
    fetchProviders();
    fetchClients();
    fetchPendingSupervisionCount();
  }, []);

  // Quick Status Toggle
  const handleToggleStatus = async (provider: Provider, nextActive: boolean) => {
    try {
      const res = await fetch(`/api/providers/${provider.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: nextActive })
      });
      if (res.ok) {
        setStatusToggleConfirm(null);
        await fetchProviders();
      }
    } catch (err) {
      console.error('Failed to toggle provider status:', err);
    }
  };

  // Filtered providers
  const filteredProviders = useMemo(() => {
    return providers.filter((p) => {
      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = p.name?.toLowerCase().includes(q);
        const matchesTitle = p.title?.toLowerCase().includes(q);
        const matchesNpi = p.npi?.toLowerCase().includes(q);
        const matchesLic = p.license_number?.toLowerCase().includes(q);
        const matchesAhcccs = p.ahcccs_id?.toLowerCase().includes(q);
        const matchesEmail = p.email?.toLowerCase().includes(q);
        if (!matchesName && !matchesTitle && !matchesNpi && !matchesLic && !matchesAhcccs && !matchesEmail) {
          return false;
        }
      }

      // Role
      if (roleFilter !== 'ALL') {
        if (roleFilter === 'SUPERVISOR') {
          if (p.role !== 'SUPERVISOR' && !p.title?.toLowerCase().includes('supervisor')) {
            return false;
          }
        } else if (p.role !== roleFilter) {
          return false;
        }
      }

      // Status
      if (statusFilter === 'ACTIVE' && p.active === 0) return false;
      if (statusFilter === 'INACTIVE' && p.active === 1) return false;

      return true;
    });
  }, [providers, searchQuery, roleFilter, statusFilter]);

  // Operational Metrics
  const activeProvidersCount = providers.filter((p) => p.active === 1).length;
  const slpCount = providers.filter((p) => p.active === 1 && (p.role === 'SLP' || p.role === 'SUPERVISOR')).length;
  const slpaCount = providers.filter((p) => p.active === 1 && p.role === 'SLPA').length;
  const totalCaseload = providers.reduce((acc, p) => acc + (p.caseload_count || 0), 0);

  // Supervisor specific team data
  const supervisedAssistants = useMemo(() => {
    if (!isSupervisor) return [];
    // Current supervisor provider
    const supervisorProv = providers.find((p) => p.role === 'SUPERVISOR' || p.title?.includes('Supervisor'));
    if (!supervisorProv) return [];
    return providers.filter((p) => p.supervisor_id === supervisorProv.id);
  }, [isSupervisor, providers]);

  // Live Slice focused full-screen mode flag for Provider Activity
  const [isLiveSliceActive, setIsLiveSliceActive] = useState(false);
  const showProvidersHeader = !(activeSubView === 'ACTIVITY' && isLiveSliceActive);

  return (
    <div className={`space-y-3.5 sm:space-y-4 ${isLiveSliceActive && activeSubView === 'ACTIVITY' ? 'w-full' : embedded ? '' : 'p-4 sm:p-5 max-w-7xl mx-auto'}`}>
      {/* Unified Header Row: Providers -> Workspace Switches -> Flexible Space -> Actions */}
      {showProvidersHeader && (
        isReferralCoordinator ? (
        <>
          {/* Mobile Header for Referral Coordinator: Canonical Steel-to-Navy Shell */}
          <div className="md:hidden relative overflow-hidden rounded-2xl border mobile-header-shell px-3.5 py-3 flex items-center justify-between gap-2 w-full">
            <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent pointer-events-none" />

            <div className="relative z-10 flex items-center space-x-2 min-w-0">
              <UserCheck className="w-5 h-5 text-sky-400 shrink-0" />
              <h1 className="text-base sm:text-lg font-bold text-white tracking-tight truncate">
                Roster
              </h1>
            </div>

            <div className="relative z-10 flex items-center space-x-1.5 shrink-0">
              <button
                onClick={() => {
                  fetchProviders();
                  fetchPendingSupervisionCount();
                }}
                disabled={refreshing}
                className="p-1.5 rounded-lg bg-gradient-to-b from-slate-800/90 to-slate-900/90 hover:from-slate-700/90 hover:to-slate-800/90 text-slate-300 hover:text-white border border-slate-700/80 text-xs font-medium flex items-center shadow-[0_2px_8px_rgba(0,0,0,0.3)] transition-all cursor-pointer active:scale-98"
                title="Refresh Roster"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin text-sky-400' : 'text-slate-400'}`} />
              </button>

              {isAdmin && (
                <button
                  onClick={() => setShowAddProviderModal(true)}
                  className="px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-900 text-xs font-semibold rounded-lg flex items-center shadow-xs border border-white/80 transition-all cursor-pointer active:scale-98 shrink-0"
                  id="mobile-add-therapist-btn"
                >
                  <span>Add Therapist</span>
                </button>
              )}
            </div>
          </div>

          {/* Desktop/Tablet Header for Referral Coordinator: Canonical Navy Shell */}
          <div className="hidden md:block relative overflow-hidden rounded-2xl border border-slate-700/80 desktop-header-shell px-6 py-4 shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]">
            {/* Subtle specular top hairline */}
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
            <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />

            <div className="relative z-10 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center flex-wrap gap-2 sm:gap-2.5 min-w-0">
                <div className="flex items-center space-x-2 mr-0.5 sm:mr-1 shrink-0">
                  <UserCheck className="w-5 h-5 text-slate-200 shrink-0" />
                  <h1 className="text-xl font-bold text-white tracking-tight">
                    Providers
                  </h1>
                </div>

                {isAdmin && (
                  <div className="flex items-center space-x-1.5 p-1 rounded-xl bg-slate-100 border border-slate-200/90">
                    {/* Clinical Provider Roster Workspace Switch */}
                    <button
                      onClick={() => setActiveSubView('ROSTER')}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold flex items-center space-x-1.5 transition-all cursor-pointer ${
                        activeSubView === 'ROSTER'
                          ? 'bg-slate-800 text-white border border-slate-700 shadow-xs'
                          : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                      }`}
                      title="Switch to Clinical Provider Roster"
                    >
                      <UserCheck className={`w-3 h-3 shrink-0 ${activeSubView === 'ROSTER' ? 'text-white' : 'text-slate-600'}`} />
                      <span className="truncate">Clinical Provider Roster</span>
                    </button>

                    {/* Role Assignments / App Users & Role Entitlements Workspace Switch */}
                    <button
                      onClick={() => {
                        setActiveSubView('APP_USERS');
                        fetchAppUsers();
                      }}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold flex items-center space-x-1.5 transition-all cursor-pointer ${
                        activeSubView === 'APP_USERS'
                          ? 'bg-slate-800 text-white border border-slate-700 shadow-xs'
                          : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                      }`}
                      title="Switch to App Users & Role Entitlements"
                    >
                      <ShieldCheck className={`w-3 h-3 shrink-0 ${activeSubView === 'APP_USERS' ? 'text-white' : 'text-slate-600'}`} />
                      <span className="truncate">App Users & Role Entitlements</span>
                    </button>

                    {/* Dedicated Provider Activity Workspace Switch */}
                    <button
                      onClick={() => setActiveSubView('ACTIVITY')}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold flex items-center space-x-1.5 transition-all cursor-pointer ${
                        activeSubView === 'ACTIVITY'
                          ? 'bg-slate-800 text-white border border-slate-700 shadow-xs'
                          : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                      }`}
                      title="Switch to Provider Activity (Owner / Referral Coordinator)"
                    >
                      <Activity className={`w-3 h-3 shrink-0 ${activeSubView === 'ACTIVITY' ? 'text-white' : 'text-slate-600'}`} />
                      <span className="truncate">Provider Activity</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Action Controls: Refresh & Therapist button */}
              <div className="flex items-center space-x-2 shrink-0">
                <button
                  onClick={() => {
                    fetchProviders();
                    fetchPendingSupervisionCount();
                  }}
                  disabled={refreshing}
                  className="p-1.5 sm:px-2.5 sm:py-1.5 rounded-lg bg-gradient-to-b from-slate-800/90 to-slate-900/90 hover:from-slate-700/90 hover:to-slate-800/90 text-slate-300 hover:text-white border border-slate-700/80 hover:border-cyan-500/40 text-xs font-medium flex items-center gap-1.5 shadow-[0_2px_8px_rgba(0,0,0,0.3)] transition-all cursor-pointer active:scale-98"
                  title="Refresh Roster"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin text-cyan-400' : 'text-slate-400'}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>

                {isAdmin && (
                  <button
                    onClick={() => setShowAddProviderModal(true)}
                    className="px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-900 text-xs font-semibold rounded-lg flex items-center space-x-1.5 border border-white/80 shadow-xs transition-all cursor-pointer active:scale-98"
                    id="add-therapist-btn"
                  >
                    <Plus className="w-3.5 h-3.5 stroke-[2.2]" />
                    <span>Therapist</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* MOBILE VIEW: Clean 2-row hierarchy in canonical steel-to-navy shell (md:hidden) */}
          <div className="md:hidden relative overflow-hidden rounded-2xl border mobile-header-shell px-3.5 py-3 flex flex-col items-center space-y-2.5 w-full">
            <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent pointer-events-none" />

            {/* ROW 1: Centered Providers Title */}
            <div className="relative z-10 flex items-center justify-center space-x-2">
              <UserCheck className="w-5 h-5 text-slate-200 shrink-0" />
              <h1 className="text-xl font-bold text-white tracking-tight">
                Providers
              </h1>
            </div>

            {/* ROW 2: [ Roster ] [ Assign Role ] [ Add Therapist ] [ Refresh ] */}
            <div className="relative z-10 flex items-center justify-center space-x-1.5 w-full flex-nowrap">
              {isAdmin && (
                <>
                  <div className="flex items-center space-x-1 p-1 rounded-xl bg-slate-100 border border-slate-200/90 shrink-0">
                  {/* Clinical Provider Roster Workspace Switch */}
                  <button
                    onClick={() => setActiveSubView('ROSTER')}
                    className={`h-7 px-2.5 rounded-lg text-[10.5px] font-semibold flex items-center space-x-1.5 transition-all cursor-pointer shrink-0 ${
                      activeSubView === 'ROSTER'
                        ? 'bg-slate-800 text-white border border-slate-700 shadow-xs'
                        : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                    }`}
                    title="Switch to Clinical Provider Roster"
                  >
                    <UserCheck className={`w-3 h-3 shrink-0 ${activeSubView === 'ROSTER' ? 'text-white' : 'text-slate-600'}`} />
                    <span className="whitespace-nowrap">Roster</span>
                  </button>

                  {/* Role Assignments Switch (Mobile visible label: Assign Role) */}
                  <button
                    onClick={() => {
                      setActiveSubView('APP_USERS');
                      fetchAppUsers();
                    }}
                    className={`h-7 px-2.5 rounded-lg text-[10.5px] font-semibold flex items-center space-x-1.5 transition-all cursor-pointer shrink-0 ${
                      activeSubView === 'APP_USERS'
                        ? 'bg-slate-800 text-white border border-slate-700 shadow-xs'
                        : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                    }`}
                    title="Switch to Role Assignments"
                  >
                    <ShieldCheck className={`w-3 h-3 shrink-0 ${activeSubView === 'APP_USERS' ? 'text-white' : 'text-slate-600'}`} />
                    <span className="whitespace-nowrap">Assign Role</span>
                  </button>
                  </div>

                  {/* Add Therapist Compact Action Button */}
                  <button
                    onClick={() => setShowAddProviderModal(true)}
                    className="h-7 px-2.5 bg-white hover:bg-slate-50 text-slate-900 text-[10.5px] font-semibold rounded-lg flex items-center justify-center shadow-xs border border-white/80 transition-all cursor-pointer active:scale-98 shrink-0 whitespace-nowrap"
                    id="add-therapist-btn-mobile"
                  >
                    <span>Add Therapist</span>
                  </button>
                </>
              )}

              {/* Refresh Action Button */}
              <button
                onClick={() => {
                  fetchProviders();
                  fetchPendingSupervisionCount();
                }}
                disabled={refreshing}
                className="h-7 w-7 rounded-lg bg-gradient-to-b from-slate-800/90 to-slate-900/90 hover:from-slate-700/90 hover:to-slate-800/90 text-slate-300 hover:text-white border border-slate-700/80 hover:border-cyan-500/40 shadow-[0_2px_8px_rgba(0,0,0,0.3)] transition-all cursor-pointer active:scale-98 flex items-center justify-center shrink-0"
                title="Refresh Roster"
                aria-label="Refresh Roster"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin text-cyan-400' : 'text-slate-400'}`} />
              </button>
            </div>
          </div>

          {/* Desktop/Tablet Header Shell: Canonical rich navy / blue-grey shell */}
          <div className="hidden md:block relative overflow-hidden rounded-2xl border border-slate-700/80 desktop-header-shell px-6 py-4 shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]">
            {/* Subtle specular top hairline */}
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
            <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />

            <div className="relative z-10 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center flex-wrap gap-2.5 min-w-0">
                <div className="flex items-center space-x-2 mr-1 shrink-0">
                  <UserCheck className="w-5 h-5 text-slate-200 shrink-0" />
                  <h1 className="text-xl font-bold text-white tracking-tight">
                    Providers
                  </h1>
                </div>

                {isAdmin && (
                  <div className="flex items-center space-x-1.5 max-w-full p-1 rounded-xl bg-slate-100 border border-slate-200/90">
                    {/* Clinical Provider Roster Workspace Switch */}
                    <button
                      onClick={() => setActiveSubView('ROSTER')}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold flex items-center space-x-1.5 transition-all cursor-pointer shrink-0 ${
                        activeSubView === 'ROSTER'
                          ? 'bg-slate-800 text-white border border-slate-700 shadow-xs'
                          : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                      }`}
                      title="Switch to Clinical Provider Roster"
                    >
                      <UserCheck className={`w-3 h-3 shrink-0 ${activeSubView === 'ROSTER' ? 'text-white' : 'text-slate-600'}`} />
                      <span className="whitespace-nowrap">Clinical Provider Roster</span>
                    </button>

                    {/* Role Assignments / App Users & Role Entitlements Workspace Switch */}
                    <button
                      onClick={() => {
                        setActiveSubView('APP_USERS');
                        fetchAppUsers();
                      }}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold flex items-center space-x-1.5 transition-all cursor-pointer shrink-0 ${
                        activeSubView === 'APP_USERS'
                          ? 'bg-slate-800 text-white border border-slate-700 shadow-xs'
                          : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                      }`}
                      title="Switch to Role Assignments"
                    >
                      <ShieldCheck className={`w-3 h-3 shrink-0 ${activeSubView === 'APP_USERS' ? 'text-white' : 'text-slate-600'}`} />
                      <span className="whitespace-nowrap">App Users & Role Entitlements</span>
                    </button>

                    {/* Dedicated Provider Activity Workspace Switch */}
                    <button
                      onClick={() => setActiveSubView('ACTIVITY')}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold flex items-center space-x-1.5 transition-all cursor-pointer shrink-0 ${
                        activeSubView === 'ACTIVITY'
                          ? 'bg-slate-800 text-white border border-slate-700 shadow-xs'
                          : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                      }`}
                      title="Switch to Provider Activity (Owner / Referral Coordinator)"
                    >
                      <Activity className={`w-3 h-3 shrink-0 ${activeSubView === 'ACTIVITY' ? 'text-white' : 'text-slate-600'}`} />
                      <span className="whitespace-nowrap">Provider Activity</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Action Controls: Refresh & Add Therapist */}
              <div className="flex items-center space-x-2 shrink-0">
                <button
                  onClick={() => {
                    fetchProviders();
                    fetchPendingSupervisionCount();
                  }}
                  disabled={refreshing}
                  className="px-2.5 py-1.5 rounded-lg bg-gradient-to-b from-slate-800/90 to-slate-900/90 hover:from-slate-700/90 hover:to-slate-800/90 text-slate-300 hover:text-white border border-slate-700/80 hover:border-cyan-500/40 text-xs font-medium flex items-center gap-1.5 shadow-[0_2px_8px_rgba(0,0,0,0.3)] transition-all cursor-pointer active:scale-98"
                  title="Refresh Roster"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin text-cyan-400' : 'text-slate-400'}`} />
                  <span>Refresh</span>
                </button>

                {isAdmin && (
                  <button
                    onClick={() => setShowAddProviderModal(true)}
                    className="px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-900 text-xs font-semibold rounded-lg flex items-center space-x-1.5 border border-white/80 shadow-xs transition-all cursor-pointer active:scale-98"
                    id="add-therapist-btn"
                  >
                    <Plus className="w-3.5 h-3.5 stroke-[2.5]" />
                    <span>Add Therapist</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      ))}

      {/* VIEW A: APP USERS & ROLE ENTITLEMENTS */}
      {activeSubView === 'APP_USERS' && (
        <div className={isReferralCoordinator ? "hidden md:block space-y-4" : "space-y-4"}>
          <div className="p-4 rounded-xl border border-slate-200/90 bg-white shadow-xs text-xs text-slate-600 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <div className="font-bold flex items-center space-x-1.5 text-slate-800 text-sm mb-1">
                <PulseCoreIcon className="w-4 h-4 text-teal-700" />
                <span>Authoritative Application Role Entitlements</span>
              </div>
              <p className="text-slate-600 text-[11px] leading-relaxed">
                OWNER is authoritative for all four application roles (OWNER, REFERRAL_COORDINATOR, SUPERVISOR, ASSISTANT).
                An app user may hold one, multiple, or all four roles to switch between operational shells.
                Switching app roles does not alter the user's canonical provider identity, license, or clinical signature authority.
              </p>
            </div>
            <div className="flex items-center space-x-2 shrink-0">
              {isOwner && (
                <button
                  type="button"
                  onClick={() => setShowAddUserModal(true)}
                  className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold flex items-center space-x-1.5 border border-slate-700 shadow-xs transition-colors"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  <span>Add App User</span>
                </button>
              )}
              <button
                onClick={fetchAppUsers}
                disabled={loadingUsers}
                className="px-3 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200/90 text-slate-700 hover:text-slate-900 rounded-lg text-xs font-medium flex items-center space-x-1.5 shrink-0 transition-colors disabled:opacity-60"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingUsers ? 'animate-spin' : ''}`} />
                <span>Refresh Users</span>
              </button>
            </div>
          </div>

          {userRolesError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{userRolesError}</span>
            </div>
          )}

          <div className="glass-panel rounded-xl border border-slate-800 mobile-ops-shell md:bg-slate-950/40 overflow-hidden">
            {/* Desktop Table (md and up) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-300">
                <thead className="bg-gradient-to-b from-[#111928] to-[#0b101b] text-slate-300 uppercase text-[9.5px] tracking-wider font-bold border-b border-slate-700/70 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
                  <tr>
                    <th className="px-5 py-2.5 font-bold">App User</th>
                    <th className="px-5 py-3 font-bold">Linked Clinical Provider</th>
                    <th className="px-5 py-3 font-bold">Role Entitlements (Click to Toggle)</th>
                    <th className="px-5 py-3 font-bold text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/70">
                  {loadingUsers && appUsers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-slate-400">
                        Loading application users...
                      </td>
                    </tr>
                  ) : appUsers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-slate-400">
                        No application users found.
                      </td>
                    </tr>
                  ) : (
                    appUsers.map((u) => {
                      const assigned: string[] = u.assignedRoles && u.assignedRoles.length > 0
                        ? u.assignedRoles
                        : [u.role];
                      const isSaving = savingUserRolesId === u.id;
                      const successMsg = userRolesSuccess[u.id];

                      const linkedProv = providers.find(
                        (p) => p.user_id === u.id || (p.id === 'PROV-JUSTIN' && u.id === 'USR-JUSTIN') || (p.email && u.email && p.email.toLowerCase() === u.email.toLowerCase())
                      );

                      return (
                        <tr key={u.id} className="hover:bg-white/[0.02] transition-colors">
                          <td className="px-5 py-4">
                            <div className="font-semibold text-slate-100 flex items-center space-x-2">
                              <span>{u.name}</span>
                              <span className="text-[10px] font-mono text-slate-500 font-normal">({u.id})</span>
                            </div>
                            <div className="text-[11px] text-slate-400 mt-0.5">{u.email}</div>
                          </td>

                          <td className="px-5 py-4">
                            {linkedProv ? (
                              <div className="flex items-center justify-between space-x-2">
                                <div className="flex items-center space-x-2">
                                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-sky-500/10 border border-sky-500/20 text-sky-300">
                                    {linkedProv.role}
                                  </span>
                                  <div>
                                    <div className="text-slate-200 font-medium text-xs">{linkedProv.name}</div>
                                    <div className="text-[10px] text-slate-400">NPI: {linkedProv.npi || 'On File'}</div>
                                  </div>
                                </div>
                                {isOwner && (
                                  <button
                                    type="button"
                                    onClick={() => setLinkModalUser({ user: u, currentProvider: linkedProv })}
                                    className="text-[10.5px] px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-sky-300 border border-slate-700 hover:border-sky-500/50 transition-colors flex items-center space-x-1 shrink-0 ml-2"
                                    title="Change or unlink clinical provider"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    <span>Edit Link</span>
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center justify-between space-x-2">
                                <span className="text-slate-500 text-[11px] italic">Operational Staff / Unlinked</span>
                                {isOwner && (
                                  <button
                                    type="button"
                                    onClick={() => setLinkModalUser({ user: u, currentProvider: null })}
                                    className="text-[10.5px] px-2 py-1 rounded bg-sky-950/60 hover:bg-sky-900/60 text-sky-300 border border-sky-800/60 hover:border-sky-600 transition-colors flex items-center space-x-1 shrink-0 ml-2"
                                    title="Link to clinical provider"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    <span>Link Provider</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </td>

                          <td className="px-5 py-4">
                            <div className="flex flex-wrap items-center gap-2">
                              {(['OWNER', 'REFERRAL_COORDINATOR', 'SUPERVISOR', 'ASSISTANT'] as const).map((rKey) => {
                                const hasRole = assigned.includes(rKey);
                                const disabled = isSaving || (!isOwner && (rKey === 'OWNER' || rKey === 'REFERRAL_COORDINATOR'));
                                const theme = getRoleEntitlementStyles(rKey);

                                let icon = null;
                                if (rKey === 'OWNER') {
                                  icon = <PulseCoreIcon className={`w-3 h-3 mr-1 shrink-0 ${hasRole ? theme.activeIcon : theme.inactiveIcon}`} />;
                                } else if (rKey === 'REFERRAL_COORDINATOR') {
                                  icon = <Crown className={`w-3 h-3 mr-1 shrink-0 ${hasRole ? theme.activeIcon : theme.inactiveIcon}`} />;
                                } else if (rKey === 'SUPERVISOR') {
                                  icon = <ShieldCheck className={`w-3 h-3 mr-1 shrink-0 ${hasRole ? theme.activeIcon : theme.inactiveIcon}`} />;
                                } else {
                                  icon = <UserCheck className={`w-3 h-3 mr-1 shrink-0 ${hasRole ? theme.activeIcon : theme.inactiveIcon}`} />;
                                }

                                return (
                                  <button
                                    key={rKey}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => handleToggleUserRole(u, rKey)}
                                    className={`group inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-all ${
                                      hasRole ? theme.activeContainer : theme.inactiveContainer
                                    } ${
                                      disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:scale-[1.03] active:scale-95'
                                    }`}
                                    title={
                                      !isOwner && (rKey === 'OWNER' || rKey === 'REFERRAL_COORDINATOR')
                                        ? 'Only OWNER can grant or revoke OWNER and REFERRAL_COORDINATOR roles'
                                        : `Click to ${hasRole ? 'remove' : 'grant'} ${rKey}`
                                    }
                                  >
                                    {icon}
                                    <span>{rKey.replace('_', ' ')}</span>
                                    {hasRole && <Check className={`w-3 h-3 ml-1 shrink-0 ${theme.activeCheck}`} />}
                                  </button>
                                );
                              })}
                            </div>
                          </td>

                          <td className="px-5 py-4 text-right">
                            <div className="flex flex-col items-end space-y-1.5">
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                                  u.active !== 0
                                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                                    : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                                }`}>
                                  {u.active !== 0 ? 'Active' : 'Deactivated'}
                                </span>
                                {isOwner && (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleUserActive(u)}
                                    disabled={savingStatusUserId === u.id || u.id === currentAuthUser?.id}
                                    className={`text-[10px] px-2 py-0.5 rounded border font-medium transition-colors ${
                                      u.active !== 0
                                        ? 'text-rose-400 border-rose-900/40 bg-rose-950/20 hover:bg-rose-900/40'
                                        : 'text-emerald-400 border-emerald-900/40 bg-emerald-950/20 hover:bg-emerald-900/40'
                                    } ${savingStatusUserId === u.id ? 'opacity-50 cursor-wait' : ''}`}
                                    title={u.id === currentAuthUser?.id ? 'Cannot deactivate your own active session' : u.active !== 0 ? 'Deactivate user login access' : 'Reactivate user login access'}
                                  >
                                    {savingStatusUserId === u.id ? '...' : u.active !== 0 ? 'Deactivate' : 'Reactivate'}
                                  </button>
                                )}
                              </div>
                              {isSaving ? (
                                <span className="text-[10px] text-purple-400 animate-pulse font-medium">Saving roles...</span>
                              ) : successMsg ? (
                                <span className="text-[10px] text-emerald-400 font-semibold flex items-center justify-end space-x-1">
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                  <span>{successMsg}</span>
                                </span>
                              ) : (
                                <span className="text-[9.5px] text-slate-500 font-mono">
                                  {assigned.length} {assigned.length === 1 ? 'role' : 'roles'}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Stacked User Cards (Under md) */}
            <div className="md:hidden divide-y divide-slate-700/60">
              {loadingUsers && appUsers.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-xs">
                  Loading application users...
                </div>
              ) : appUsers.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-xs">
                  No application users found.
                </div>
              ) : (
                appUsers.map((u, idx) => {
                  const assigned: string[] = u.assignedRoles && u.assignedRoles.length > 0
                    ? u.assignedRoles
                    : [u.role];
                  const isSaving = savingUserRolesId === u.id;
                  const successMsg = userRolesSuccess[u.id];

                  const linkedProv = providers.find(
                    (p) => p.user_id === u.id || (p.id === 'PROV-JUSTIN' && u.id === 'USR-JUSTIN') || (p.email && u.email && p.email.toLowerCase() === u.email.toLowerCase())
                  );

                  return (
                    <div key={`mb-app-usr-${u.id}`} className={`p-3.5 space-y-2.5 ${idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'} md:bg-slate-950/40 text-xs`}>
                      {/* Line 1: User Identity, Status & Deactivation Toggle */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-white text-[13px] flex items-center gap-1.5 flex-wrap">
                            <span>{u.name}</span>
                            <span className="text-[10px] font-mono text-slate-500 font-normal">({u.id})</span>
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5 truncate">{u.email}</div>
                        </div>

                        <div className="shrink-0 flex flex-col items-end gap-1">
                          <div className="flex items-center space-x-1.5">
                            <span className={`inline-flex items-center px-1.5 py-0.2 rounded-full text-[9px] font-semibold border ${
                              u.active !== 0
                                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                                : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                            }`}>
                              {u.active !== 0 ? 'Active' : 'Deactivated'}
                            </span>
                            {isOwner && (
                              <button
                                type="button"
                                onClick={() => handleToggleUserActive(u)}
                                disabled={savingStatusUserId === u.id || u.id === currentAuthUser?.id}
                                className={`text-[9.5px] px-1.5 py-0.5 rounded border font-medium ${
                                  u.active !== 0
                                    ? 'text-rose-400 border-rose-900/40 bg-rose-950/20'
                                    : 'text-emerald-400 border-emerald-900/40 bg-emerald-950/20'
                                }`}
                              >
                                {savingStatusUserId === u.id ? '...' : u.active !== 0 ? 'Deactivate' : 'Reactivate'}
                              </button>
                            )}
                          </div>
                          {isSaving ? (
                            <span className="text-[9.5px] text-purple-400 animate-pulse font-medium">Saving...</span>
                          ) : successMsg ? (
                            <span className="text-[9.5px] text-emerald-400 font-semibold flex items-center justify-end space-x-0.5">
                              <CheckCircle2 className="w-3 h-3" />
                              <span>{successMsg}</span>
                            </span>
                          ) : (
                            <span className="text-[9px] text-slate-500 font-mono">
                              {assigned.length} {assigned.length === 1 ? 'role' : 'roles'}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Line 2: Linked Clinical Provider & Link Button */}
                      <div className="text-[11.5px] pt-1 border-t border-slate-800/60 flex items-center justify-between gap-1 flex-wrap">
                        {linkedProv ? (
                          <div className="flex items-center gap-1.5 text-sky-300 flex-wrap">
                            <span className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">Linked:</span>
                            <span className="px-1.5 py-0.2 rounded bg-sky-500/10 border border-sky-500/20 text-[9.5px] font-bold">
                              {linkedProv.role}
                            </span>
                            <span className="text-slate-200 font-medium">{linkedProv.name}</span>
                            <span className="text-slate-500 text-[10px] font-mono">NPI: {linkedProv.npi || 'On File'}</span>
                          </div>
                        ) : (
                          <span className="text-slate-500 text-[11px] italic">Operational Staff / Unlinked</span>
                        )}

                        {isOwner && (
                          <button
                            type="button"
                            onClick={() => setLinkModalUser({ user: u, currentProvider: linkedProv || null })}
                            className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-sky-300 border border-slate-700 flex items-center space-x-1"
                          >
                            <Link2 className="w-2.5 h-2.5" />
                            <span>{linkedProv ? 'Edit Link' : 'Link'}</span>
                          </button>
                        )}
                      </div>

                      {/* Line 3: Role Entitlements Grid */}
                      <div className="pt-1.5 border-t border-slate-800/60">
                        <div className="text-[9.5px] uppercase font-bold text-slate-400 tracking-wider mb-1.5">
                          Role Entitlements:
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          {(['OWNER', 'REFERRAL_COORDINATOR', 'SUPERVISOR', 'ASSISTANT'] as const).map((rKey) => {
                            const hasRole = assigned.includes(rKey);
                            const disabled = isSaving || (!isOwner && (rKey === 'OWNER' || rKey === 'REFERRAL_COORDINATOR'));
                            const theme = getRoleEntitlementStyles(rKey);

                            let icon = null;
                            if (rKey === 'OWNER') {
                              icon = <PulseCoreIcon className={`w-3 h-3 mr-1 shrink-0 ${hasRole ? theme.activeIcon : theme.inactiveIcon}`} />;
                            } else if (rKey === 'REFERRAL_COORDINATOR') {
                              icon = <Crown className={`w-3 h-3 mr-1 shrink-0 ${hasRole ? theme.activeIcon : theme.inactiveIcon}`} />;
                            } else if (rKey === 'SUPERVISOR') {
                              icon = <ShieldCheck className={`w-3 h-3 mr-1 shrink-0 ${hasRole ? theme.activeIcon : theme.inactiveIcon}`} />;
                            } else {
                              icon = <UserCheck className={`w-3 h-3 mr-1 shrink-0 ${hasRole ? theme.activeIcon : theme.inactiveIcon}`} />;
                            }

                            return (
                              <button
                                key={rKey}
                                type="button"
                                disabled={disabled}
                                onClick={() => handleToggleUserRole(u, rKey)}
                                className={`inline-flex items-center justify-between px-2 py-1.5 rounded-md text-[10.5px] font-semibold border transition-all ${
                                  hasRole ? theme.activeContainer : theme.inactiveContainer
                                } ${
                                  disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer active:scale-95'
                                }`}
                              >
                                <div className="flex items-center truncate min-w-0">
                                  {icon}
                                  <span className="truncate">{rKey.replace('_', ' ')}</span>
                                </div>
                                {hasRole && <Check className={`w-3 h-3 ml-1 shrink-0 ${theme.activeCheck}`} />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* VIEW B: CLINICAL PROVIDER ROSTER */}
      {activeSubView === 'ROSTER' && (
        <>
          {/* Compact Operational KPI Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {/* Active Therapists */}
            <div className="relative rounded-xl border border-slate-200/90 bg-white mobile-ops-kdi px-3 py-2 shadow-2xs overflow-hidden">
              <div className="text-[10px] uppercase font-bold text-slate-900 tracking-wider flex items-center justify-between">
                <span>Active Therapists</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              </div>
              <div className="flex items-baseline space-x-1.5 mt-0.5">
                <span className="text-lg font-black font-mono text-slate-900 tracking-tight">{activeProvidersCount}</span>
                <span className="text-[10.5px] text-slate-600 font-normal">/ {providers.length} total</span>
              </div>
            </div>

            {/* Licensed SLPs */}
            <div className="relative rounded-xl border border-slate-200/90 bg-white mobile-ops-kdi px-3 py-2 shadow-2xs overflow-hidden">
              <div className="text-[10px] uppercase font-bold text-slate-900 tracking-wider">Licensed SLPs</div>
              <div className="flex items-baseline mt-0.5">
                <span className="text-lg font-black font-mono text-sky-700 tracking-tight">{slpCount}</span>
              </div>
            </div>

            {/* Speech Assistants (SLPA) */}
            <div className="relative rounded-xl border border-slate-200/90 bg-white mobile-ops-kdi px-3 py-2 shadow-2xs overflow-hidden">
              <div className="text-[10px] uppercase font-bold text-slate-900 tracking-wider">Speech Assistants (SLPA)</div>
              <div className="flex items-baseline mt-0.5">
                <span className="text-lg font-black font-mono text-slate-800 tracking-tight">{slpaCount}</span>
              </div>
            </div>

            {/* Total Active Caseload */}
            <div className="relative rounded-xl border border-slate-200/90 bg-white mobile-ops-kdi px-3 py-2 shadow-2xs overflow-hidden">
              <div className="text-[10px] uppercase font-bold text-slate-900 tracking-wider">Total Active Caseload</div>
              <div className="flex items-baseline space-x-1.5 mt-0.5">
                <span className="text-lg font-black font-mono text-emerald-700 tracking-tight">{totalCaseload}</span>
                <span className="text-[10.5px] text-slate-600 font-normal">clients</span>
              </div>
            </div>
          </div>

          {/* Balanced Command Row */}
          <div className="p-2 sm:p-2.5 rounded-xl border border-slate-200/90 bg-white shadow-xs flex flex-col md:flex-row gap-2 md:items-center justify-between">
            {/* Search with ArcSearchInput (Pulse) */}
            <div className="w-full md:w-80 lg:w-96 shrink-0">
              <ArcSearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search roster..."
                className="w-full"
              />
            </div>

            {/* Role & Status Filter Pills */}
            <div className="flex flex-wrap items-center gap-1.5 w-full md:w-auto md:justify-end">
              {/* Role selector */}
              <div className="flex items-center gap-1 p-1 rounded-xl bg-slate-100/90 border border-slate-200/90 shadow-xs">
                {(['ALL', 'SLP', 'SLPA', 'SUPERVISOR'] as const).map((r) => {
                  const isSelected = roleFilter === r;
                  const label = r === 'ALL' ? 'All Roles' : r === 'SUPERVISOR' ? 'Supervisor' : r;
                  return (
                    <button
                      key={r}
                      onClick={() => setRoleFilter(r)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold tracking-tight transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-slate-800 text-white font-semibold border border-slate-700 shadow-xs'
                          : 'bg-white text-slate-700 hover:text-slate-950 hover:bg-slate-50 border border-slate-200/90 shadow-xs'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {/* Status selector */}
              <div className="flex items-center gap-1 p-1 rounded-xl bg-slate-100/90 border border-slate-200/90 shadow-xs">
                {(['ACTIVE', 'INACTIVE', 'ALL'] as const).map((s) => {
                  const isSelected = statusFilter === s;
                  const label = s === 'ACTIVE' ? 'Active' : s === 'INACTIVE' ? 'Inactive' : 'All';
                  return (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold tracking-tight transition-all cursor-pointer ${
                        isSelected
                          ? s === 'ACTIVE'
                            ? 'bg-emerald-50 text-emerald-800 font-semibold border border-emerald-300 shadow-xs'
                            : s === 'INACTIVE'
                            ? 'bg-amber-50 text-amber-800 font-semibold border border-amber-300 shadow-xs'
                            : 'bg-slate-800 text-white font-semibold border border-slate-700 shadow-xs'
                          : 'bg-white text-slate-700 hover:text-slate-950 hover:bg-slate-50 border border-slate-200/90 shadow-xs'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Roster Table - Densified (2x density) */}
          <div className="rounded-xl overflow-hidden border border-slate-200/90 bg-white shadow-xs">
            {/* Mobile Provider Rows / Cards: Compact wrapped layout, no horizontal overflow */}
            <div className="md:hidden divide-y divide-slate-800/80">
              {loading ? (
                <div className="p-6 text-center text-slate-400">
                  <div className="flex items-center justify-center space-x-2">
                    <RefreshCw className="w-4 h-4 animate-spin text-cyan-400" />
                    <span>Loading provider roster...</span>
                  </div>
                </div>
              ) : filteredProviders.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-xs">
                  No providers found matching current filters.
                </div>
              ) : (
                filteredProviders.map((p, idx: number) => {
                  const isProvActive = p.active === 1;
                  const initials = p.name
                    .split(' ')
                    .map((n) => n[0])
                    .filter(Boolean)
                    .slice(0, 2)
                    .join('');

                  return (
                    <div
                      key={`mobile-prov-${p.id}`}
                      onClick={() => setSelectedProviderIdForProfile(p.id)}
                      className={`p-3 transition-colors cursor-pointer select-none border-l-2 border-l-transparent hover:border-l-cyan-400/80 hover:bg-[#121d2e] active:bg-[#17243a] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] ${
                        !isProvActive
                          ? 'opacity-70 odd:bg-slate-900/40 even:bg-slate-900/20'
                          : idx % 2 === 0
                          ? 'mobile-ops-row-a'
                          : 'mobile-ops-row-b'
                      }`}
                    >
                      {/* Row 1: [Avatar] Provider Name */}
                      <div className="flex items-center space-x-2.5 min-w-0">
                        <div
                          className={`w-7 h-7 rounded-lg flex items-center justify-center font-mono font-bold text-xs shrink-0 border shadow-xs ${
                            p.role === 'SLPA'
                              ? 'bg-indigo-950/70 text-indigo-300 border-indigo-500/40 shadow-[0_0_8px_rgba(99,102,241,0.15)]'
                              : p.role === 'SUPERVISOR'
                              ? 'bg-amber-950/70 text-amber-300 border-amber-500/40 shadow-[0_0_8px_rgba(245,158,11,0.15)]'
                              : 'bg-cyan-950/70 text-cyan-300 border-cyan-500/40 shadow-[0_0_8px_rgba(6,182,212,0.15)]'
                          }`}
                        >
                          {initials}
                        </div>
                        <div className="font-bold text-slate-100 text-sm truncate min-w-0">
                          {p.name}
                        </div>
                      </div>

                      {/* Row 2: [ N client(s) ] [ ACTIVE / INACTIVE ] [ Detail > ] */}
                      <div className="mt-2 pt-2 border-t border-slate-800/50 flex items-center justify-between gap-2">
                        <div className="flex items-center space-x-2 shrink-0">
                          {/* Caseload Count */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProviderForCaseload(p);
                            }}
                            className="px-2 py-0.5 rounded-md bg-slate-100 border border-slate-300/90 hover:bg-slate-200 hover:border-slate-400 text-slate-800 hover:text-slate-950 font-mono text-[10.5px] font-semibold flex items-center gap-1 shrink-0 cursor-pointer active:scale-95"
                            title="Manage caseload assignments"
                          >
                            <Users className="w-3 h-3 text-slate-600" />
                            <span>{p.caseload_count || 0}</span>
                            <span className="text-[9.5px] text-slate-600 font-normal">
                              {(p.caseload_count || 0) === 1 ? 'client' : 'clients'}
                            </span>
                          </button>

                          {/* Active / Inactive Status Badge */}
                          {isProvActive ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-mono font-bold bg-emerald-950/70 text-emerald-300 border border-emerald-500/35 shadow-[0_0_6px_rgba(16,185,129,0.15)] leading-none">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                              ACTIVE
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-mono font-bold bg-amber-950/70 text-amber-300 border border-amber-500/35 leading-none">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                              INACTIVE
                            </span>
                          )}
                        </div>

                        {/* Detail Action */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProviderIdForProfile(p.id);
                          }}
                          className="px-2.5 py-1 rounded-md bg-cyan-950/70 hover:bg-cyan-900/80 text-cyan-300 hover:text-cyan-200 border border-cyan-500/40 text-[11px] font-semibold flex items-center gap-1 cursor-pointer active:scale-95 shrink-0"
                        >
                          <span>Detail</span>
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Desktop/Tablet Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-[9.5px] uppercase font-bold text-slate-700 border-b border-slate-200">
                  <tr>
                    <th className="px-3.5 py-2 font-bold tracking-wider">Provider</th>
                    <th className="hidden xl:table-cell px-3.5 py-2 font-bold tracking-wider">Role & Title</th>
                    <th className="hidden 2xl:table-cell px-3.5 py-2 font-bold tracking-wider">Credentials</th>
                    <th className="hidden 2xl:table-cell px-3.5 py-2 font-bold tracking-wider">Supervision</th>
                    <th className="px-3.5 py-2 font-bold tracking-wider whitespace-nowrap">Active Caseload</th>
                    <th className="px-3.5 py-2 font-bold tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-3.5 py-2 font-bold tracking-wider text-right whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-6 text-center text-slate-600">
                        <div className="flex items-center justify-center space-x-2">
                          <RefreshCw className="w-4 h-4 animate-spin text-cyan-400" />
                          <span>Loading provider roster...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredProviders.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-6 text-center text-slate-600">
                        No providers found matching current filters.
                      </td>
                    </tr>
                  ) : (
                    filteredProviders.map((p, idx: number) => {
                      const isProvActive = p.active === 1;
                      const initials = p.name
                        .split(' ')
                        .map((n) => n[0])
                        .filter(Boolean)
                        .slice(0, 2)
                        .join('');

                      return (
                        <tr
                          key={p.id}
                          className={`transition-all duration-150 group border-l-2 border-l-transparent hover:border-l-slate-400 hover:bg-[#20314a] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] ${
                            !isProvActive
                              ? 'opacity-70 odd:bg-slate-900/30 even:bg-slate-900/10'
                              : idx % 2 === 0
                              ? 'ops-row-a'
                              : 'ops-row-b'
                          }`}
                        >
                          {/* Name & Identity */}
                          <td className="px-3.5 py-2">
                            <div className="flex items-center space-x-2.5">
                              <div
                                className="w-7 h-7 rounded-md flex items-center justify-center font-mono font-bold text-[11px] shrink-0 border border-slate-200 bg-slate-100 text-slate-700 shadow-2xs"
                              >
                                {initials}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-bold text-slate-900 text-xs flex items-center space-x-1.5 leading-none">
                                  <span className="truncate">{p.name}</span>
                                  <span className="text-[9.5px] font-mono text-slate-600 font-normal shrink-0 hidden 2xl:inline">
                                    ({p.id})
                                  </span>
                                </div>
                                <div className="text-[10px] text-slate-600 mt-1 flex items-center gap-2 leading-none hidden 2xl:flex">
                                  {p.email && (
                                    <span className="flex items-center gap-1 truncate max-w-[160px]">
                                      <Mail className="w-2.5 h-2.5 text-slate-400 shrink-0" />
                                      <span className="truncate">{p.email}</span>
                                    </span>
                                  )}
                                  {p.phone && (
                                    <span className="flex items-center gap-1 shrink-0">
                                      <Phone className="w-2.5 h-2.5 text-slate-400 shrink-0" />
                                      <span>{p.phone}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Role & Title */}
                          <td className="hidden xl:table-cell px-3.5 py-2">
                            <div className="flex items-center space-x-1.5">
                              <span
                                className="text-[9.5px] font-mono font-bold px-1.5 py-0.5 rounded border border-slate-200/90 bg-slate-100 text-slate-700 inline-block leading-none"
                              >
                                {p.role}
                              </span>
                            </div>
                            <div className="text-[10.5px] text-slate-600 mt-1 truncate max-w-[150px] leading-tight">
                              {p.title}
                            </div>
                          </td>

                          {/* Credentials */}
                          <td className="hidden 2xl:table-cell px-3.5 py-2 font-mono text-[10.5px]">
                            <div className="text-slate-800 font-medium leading-tight">NPI: {p.npi || 'On File'}</div>
                            <div className="text-slate-600 text-[10px] mt-0.5 leading-tight">
                              Lic: {p.license_number || 'AZ-Active'}
                            </div>
                            {p.ahcccs_id && (
                              <div className="text-slate-600 text-[10px] mt-0.5 leading-tight font-medium">
                                AHCCCS: {p.ahcccs_id}
                              </div>
                            )}
                          </td>

                          {/* Supervision */}
                          <td className="hidden 2xl:table-cell px-3.5 py-2">
                            {p.role === 'SLPA' ? (
                              p.supervisor_name ? (
                                <div>
                                  <div className="flex items-center gap-1 text-slate-800 text-[11px] font-semibold leading-tight">
                                    <ShieldCheck className="w-3 h-3 text-slate-500 shrink-0" />
                                    <span className="truncate max-w-[130px]">{p.supervisor_name}</span>
                                  </div>
                                  <span className="text-[9.5px] text-slate-600 block leading-tight mt-0.5">Designated Supervisor</span>
                                </div>
                              ) : (
                                <span className="text-amber-900 text-[10px] font-semibold flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 leading-none">
                                  <AlertCircle className="w-3 h-3 text-amber-700 shrink-0" />
                                  <span>Needs Supervisor</span>
                                </span>
                              )
                            ) : p.role === 'SUPERVISOR' || p.title?.includes('Supervisor') ? (
                              <div>
                                <div className="flex items-center gap-1 text-slate-800 text-[11px] font-semibold leading-tight">
                                  <ShieldCheck className="w-3 h-3 text-slate-500 shrink-0" />
                                  <span>Clinical Supervisor</span>
                                </div>
                                <span className="text-[9.5px] text-slate-600 block leading-tight mt-0.5">Lead SLP / Co-Signer</span>
                              </div>
                            ) : (
                              <span className="text-slate-600 text-[10.5px] font-medium">Independent SLP</span>
                            )}
                          </td>

                          {/* Caseload Count */}
                          <td className="px-3.5 py-2 whitespace-nowrap">
                            <button
                              onClick={() => setSelectedProviderForCaseload(p)}
                              className="px-2 py-0.5 rounded border border-slate-200/90 bg-slate-100 hover:bg-slate-200/80 text-slate-800 font-mono text-[11px] font-semibold flex items-center gap-1 shadow-2xs transition-colors cursor-pointer group/case leading-none"
                              title="Manage caseload assignments"
                            >
                              <Users className="w-3 h-3 text-slate-500 group-hover/case:text-slate-700 transition-colors" />
                              <span>{p.caseload_count || 0}</span>
                              <span className="text-[9.5px] text-slate-500 font-normal">pts</span>
                            </button>
                          </td>

                          {/* Status & Lifecycle */}
                          <td className="px-3.5 py-2 whitespace-nowrap">
                            {isProvActive ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-mono font-bold bg-emerald-50 text-emerald-800 border border-emerald-300 shadow-2xs leading-none">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />
                                ACTIVE
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-mono font-bold bg-slate-100 text-slate-600 border border-slate-200/90 shadow-2xs leading-none">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                                INACTIVE
                              </span>
                            )}
                          </td>

                          {/* Primary Actions */}
                          <td className="px-3.5 py-2 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end space-x-1.5">
                              <button
                                onClick={() => setSelectedProviderIdForProfile(p.id)}
                                className="px-2 py-0.5 rounded border border-slate-300/90 bg-white hover:bg-slate-50 text-slate-800 text-[10.5px] font-semibold transition-all cursor-pointer shadow-2xs active:scale-98 leading-none"
                              >
                                Profile
                              </button>

                              <button
                                onClick={() => setSelectedProviderForCaseload(p)}
                                className="px-2 py-0.5 rounded border border-slate-200/90 bg-slate-100 hover:bg-slate-200/80 text-slate-800 text-[10.5px] font-semibold transition-all cursor-pointer shadow-2xs active:scale-98 leading-none"
                              >
                                Caseload
                              </button>

                              {isAdmin && (
                                <>
                                  <button
                                    onClick={() => setProviderToEdit(p)}
                                    className="p-1 rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer"
                                    title="Edit Provider Details & Supervision"
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </button>

                                  <button
                                    onClick={() =>
                                      setStatusToggleConfirm({
                                        provider: p,
                                        nextActive: !isProvActive
                                      })
                                    }
                                    className={`p-1 rounded transition-colors cursor-pointer ${
                                      isProvActive
                                        ? 'text-slate-400 hover:text-amber-400 hover:bg-amber-500/10'
                                        : 'text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10'
                                    }`}
                                    title={isProvActive ? 'Deactivate Provider' : 'Reactivate Provider'}
                                  >
                                    <CheckCircle2 className="w-3 h-3" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Secondary Collapsible: App Users & Role Entitlements (Referral Coordinator Mobile) */}
          {isReferralCoordinator && (
            <div className="md:hidden mt-4">
              <div className="relative rounded-xl border border-slate-200/90 bg-white overflow-hidden shadow-xs">
                <button
                  type="button"
                  onClick={() => {
                    const next = !mobileAppUsersExpanded;
                    setMobileAppUsersExpanded(next);
                    if (next && appUsers.length === 0 && !loadingUsers) {
                      fetchAppUsers();
                    }
                  }}
                  className="w-full p-3.5 flex items-center justify-between gap-3 text-left bg-white hover:bg-slate-50/80 transition-colors cursor-pointer"
                >
                  <div className="flex items-center space-x-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200/90 flex items-center justify-center shrink-0">
                      <ShieldCheck className="w-4 h-4 text-teal-700" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                        <span className="truncate">App Users & Role Entitlements</span>
                        <span className="text-[9px] px-1.5 py-0.2 rounded bg-slate-100 text-slate-600 border border-slate-200/90 font-mono shrink-0 font-semibold">
                          Secondary
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-600 mt-0.5 truncate">
                        Application role visibility & entitlements
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 shrink-0">
                    <ChevronDown
                      className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${
                        mobileAppUsersExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>

                {mobileAppUsersExpanded && (
                  <div className="p-3.5 border-t border-slate-200/90 space-y-3 bg-slate-50/80">
                    <div className="flex items-center justify-between text-xs pb-2 border-b border-slate-200/90">
                      <span className="text-[11px] text-slate-600 font-medium">Application User Roster</span>
                      <button
                        type="button"
                        onClick={fetchAppUsers}
                        disabled={loadingUsers}
                        className="px-2 py-1 rounded-md bg-white hover:bg-slate-50 border border-slate-200/90 text-slate-700 hover:text-slate-900 text-[10px] font-semibold flex items-center space-x-1 cursor-pointer transition-colors shadow-2xs disabled:opacity-60"
                      >
                        <RefreshCw className={`w-3 h-3 ${loadingUsers ? 'animate-spin' : ''}`} />
                        <span>Refresh</span>
                      </button>
                    </div>

                    {loadingUsers && appUsers.length === 0 ? (
                      <div className="py-6 text-center text-slate-600 text-xs flex items-center justify-center gap-2">
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-teal-700" />
                        <span>Loading app users...</span>
                      </div>
                    ) : appUsers.length === 0 ? (
                      <div className="py-6 text-center text-slate-600 text-xs">
                        No application users found.
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        {appUsers.map((u, uIdx: number) => {
                          const assigned: string[] = u.assignedRoles && u.assignedRoles.length > 0
                            ? u.assignedRoles
                            : [u.role];
                          const isSaving = savingUserRolesId === u.id;
                          const successMsg = userRolesSuccess[u.id];
                          const linkedProv = providers.find(
                            (p) => p.user_id === u.id || (p.id === 'PROV-JUSTIN' && u.id === 'USR-JUSTIN') || (p.email && u.email && p.email.toLowerCase() === u.email.toLowerCase())
                          );

                          return (
                            <div
                              key={`mobile-u-${u.id}`}
                              className={`p-3 rounded-lg border border-slate-200/90 ${
                                uIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                              } space-y-2 text-xs shadow-2xs`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="font-semibold text-slate-800 flex items-center gap-1.5 flex-wrap">
                                    <span>{u.name}</span>
                                    <span className="text-[9.5px] font-mono text-slate-500 font-normal">({u.id})</span>
                                  </div>
                                  <div className="text-[10.5px] text-slate-600 mt-0.5">{u.email}</div>
                                </div>
                                {isSaving ? (
                                  <span className="text-[10px] text-teal-700 animate-pulse font-medium">Saving...</span>
                                ) : successMsg ? (
                                  <span className="text-[10px] text-emerald-700 font-semibold flex items-center space-x-0.5">
                                    <CheckCircle2 className="w-3 h-3" />
                                    <span>{successMsg}</span>
                                  </span>
                                ) : null}
                              </div>

                              <div className="text-[11px]">
                                {linkedProv ? (
                                  <div className="flex items-center gap-1.5 text-slate-700">
                                    <span className="text-slate-500 text-[10px]">Linked:</span>
                                    <span className="px-1.5 py-0.2 rounded bg-teal-50 border border-teal-200/80 text-teal-800 text-[9.5px] font-bold">
                                      {linkedProv.role}
                                    </span>
                                    <span className="text-slate-800 font-medium">{linkedProv.name}</span>
                                  </div>
                                ) : (
                                  <span className="text-slate-500 text-[10.5px] italic">Operational Staff / Unlinked</span>
                                )}
                              </div>

                              <div className="mt-2 pt-2 border-t border-slate-200/90">
                                <div className="text-[10px] text-slate-600 mb-1 font-semibold uppercase tracking-wider">
                                  Role Entitlements
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {(['OWNER', 'REFERRAL_COORDINATOR', 'SUPERVISOR', 'ASSISTANT'] as const).map((rKey) => {
                                    const hasRole = assigned.includes(rKey);
                                    const disabled = isSaving || (!isOwner && (rKey === 'OWNER' || rKey === 'REFERRAL_COORDINATOR'));
                                    const theme = getRoleEntitlementStyles(rKey);

                                    let icon = null;
                                    if (rKey === 'OWNER') {
                                      icon = <PulseCoreIcon className={`w-2.5 h-2.5 mr-1 shrink-0 ${hasRole ? theme.activeIcon : theme.inactiveIcon}`} />;
                                    } else if (rKey === 'REFERRAL_COORDINATOR') {
                                      icon = <Crown className={`w-2.5 h-2.5 mr-1 shrink-0 ${hasRole ? theme.activeIcon : theme.inactiveIcon}`} />;
                                    } else if (rKey === 'SUPERVISOR') {
                                      icon = <ShieldCheck className={`w-2.5 h-2.5 mr-1 shrink-0 ${hasRole ? theme.activeIcon : theme.inactiveIcon}`} />;
                                    } else {
                                      icon = <UserCheck className={`w-2.5 h-2.5 mr-1 shrink-0 ${hasRole ? theme.activeIcon : theme.inactiveIcon}`} />;
                                    }

                                    return (
                                      <button
                                        key={rKey}
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => handleToggleUserRole(u, rKey)}
                                        className={`group inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border transition-all ${
                                          hasRole ? theme.activeContainer : theme.inactiveContainer
                                        } ${
                                          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer active:scale-95'
                                        }`}
                                      >
                                        {icon}
                                        <span>{rKey === 'REFERRAL_COORDINATOR' ? 'Ref Coord' : rKey.replace('_', ' ')}</span>
                                        {hasRole && <Check className={`w-2.5 h-2.5 ml-1 shrink-0 ${theme.activeCheck}`} />}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
      </>
      )}

      {/* VIEW C: PROVIDER ACTIVITY WORKSPACE (Dedicated Operational Review & Live Slice) */}
      {activeSubView === 'ACTIVITY' && (
        <ProviderActivityView
          userRole={userRole}
          onNavigateTab={onNavigateToTab}
          onSelectClient={onSelectClient}
          embedded={true}
          onLiveSliceActiveChange={setIsLiveSliceActive}
        />
      )}

      {/* MODAL 1: Provider Profile View */}
      {selectedProviderIdForProfile && (
        <ProviderDetailModal
          providerId={selectedProviderIdForProfile}
          userRole={userRole}
          onClose={() => setSelectedProviderIdForProfile(null)}
          onRefreshList={fetchProviders}
          onEdit={(prov) => {
            setSelectedProviderIdForProfile(null);
            setProviderToEdit(prov);
          }}
        />
      )}

      {/* MODAL 2: Add Provider Modal */}
      {showAddProviderModal && (
        <AddEditProviderModal
          activeSupervisors={providers.filter((p) => p.active === 1 && (p.role === 'SLP' || p.role === 'SUPERVISOR') && !p.requires_supervision)}
          onClose={() => setShowAddProviderModal(false)}
          onSaved={async () => {
            setShowAddProviderModal(false);
            await fetchProviders();
          }}
        />
      )}

      {/* MODAL 3: Edit Provider Modal */}
      {providerToEdit && (
        <AddEditProviderModal
          initialProvider={providerToEdit}
          activeSupervisors={providers.filter((p) => p.active === 1 && (p.role === 'SLP' || p.role === 'SUPERVISOR') && !p.requires_supervision && p.id !== providerToEdit.id)}
          onClose={() => setProviderToEdit(null)}
          onSaved={async () => {
            setProviderToEdit(null);
            await fetchProviders();
          }}
        />
      )}

      {/* MODAL 4: Caseload Manager Modal (Bulk & Individual) */}
      {selectedProviderForCaseload && (
        <CaseloadManagerModal
          provider={selectedProviderForCaseload}
          allProviders={providers.filter((p) => p.active === 1 && p.id !== selectedProviderForCaseload.id)}
          allClients={clients}
          isAdmin={isAdmin}
          onClose={() => setSelectedProviderForCaseload(null)}
          onUpdated={async () => {
            await fetchProviders();
            await fetchClients();
          }}
        />
      )}

      {/* MODAL 5: Add App User Modal */}
      {showAddUserModal && (
        <AddAppUserModal
          onClose={() => setShowAddUserModal(false)}
          onCreated={async () => {
            setShowAddUserModal(false);
            await Promise.all([fetchAppUsers(), fetchProviders()]);
          }}
          providers={providers}
          existingUsers={appUsers}
        />
      )}

      {/* MODAL 6: Link / Unlink Clinical Provider Modal */}
      {linkModalUser && (
        <LinkProviderModal
          user={linkModalUser.user}
          currentProvider={linkModalUser.currentProvider}
          providers={providers}
          existingUsers={appUsers}
          onClose={() => setLinkModalUser(null)}
          onSaved={async () => {
            setLinkModalUser(null);
            await Promise.all([fetchAppUsers(), fetchProviders()]);
          }}
        />
      )}

      {/* MODAL 5: Status Toggle Confirmation */}
      {statusToggleConfirm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-panel max-w-sm w-full rounded-xl border border-slate-800 p-5 space-y-4">
            <div className="flex items-center space-x-2 text-amber-400">
              <AlertCircle className="w-5 h-5" />
              <h3 className="font-bold text-sm text-white">
                {statusToggleConfirm.nextActive ? 'Reactivate Provider?' : 'Deactivate Provider?'}
              </h3>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
              {statusToggleConfirm.nextActive
                ? `Reactivating ${statusToggleConfirm.provider.name} will restore them to active assignment dropdowns and caseload workflows.`
                : `Deactivating ${statusToggleConfirm.provider.name} will mute them from new client and supervisor assignment selectors. Historical documentation, notes, and past claims remain permanently preserved.`}
            </p>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                onClick={() => setStatusToggleConfirm(null)}
                className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handleToggleStatus(statusToggleConfirm.provider, statusToggleConfirm.nextActive)
                }
                className={`px-3 py-1.5 rounded text-xs font-bold text-white transition-colors ${
                  statusToggleConfirm.nextActive
                    ? 'bg-emerald-600 hover:bg-emerald-500'
                    : 'bg-amber-600 hover:bg-amber-500'
                }`}
              >
                {statusToggleConfirm.nextActive ? 'Confirm Reactivation' : 'Confirm Deactivation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------
// ADD / EDIT PROVIDER MODAL COMPONENT
// ----------------------------------------------------------------------
interface AddEditProviderModalProps {
  initialProvider?: Provider;
  activeSupervisors: Provider[];
  onClose: () => void;
  onSaved: () => void;
}

const AddEditProviderModal: React.FC<AddEditProviderModalProps> = ({
  initialProvider,
  activeSupervisors,
  onClose,
  onSaved
}) => {
  const isEditing = !!initialProvider;

  const [name, setName] = useState(initialProvider?.name || '');
  const [firstName, setFirstName] = useState(initialProvider?.first_name || '');
  const [middleName, setMiddleName] = useState(initialProvider?.middle_name || '');
  const [lastName, setLastName] = useState(initialProvider?.last_name || '');
  const [suffix, setSuffix] = useState(initialProvider?.suffix || '');
  const [ediCredentials, setEdiCredentials] = useState(initialProvider?.credentials || '');
  const [role, setRole] = useState<'SLP' | 'SLPA' | 'SUPERVISOR'>(
    (initialProvider?.role as any) || 'SLP'
  );
  const [title, setTitle] = useState(
    initialProvider?.title || 'Speech-Language Pathologist'
  );
  const [email, setEmail] = useState(initialProvider?.email || '');
  const [phone, setPhone] = useState(initialProvider?.phone || '');
  const [npi, setNpi] = useState(initialProvider?.npi || '');
  const [licenseNumber, setLicenseNumber] = useState(
    initialProvider?.license_number || ''
  );
  const [ahcccsId, setAhcccsId] = useState(
    initialProvider?.ahcccs_id || ''
  );
  const [supervisorId, setSupervisorId] = useState(
    initialProvider?.supervisor_id || ''
  );
  const [requiresSupervision, setRequiresSupervision] = useState(
    Boolean(initialProvider?.requires_supervision)
  );
  const [ratePerSession, setRatePerSession] = useState<number | ''>(
    initialProvider?.rate_per_session !== undefined &&
      initialProvider?.rate_per_session !== null
      ? initialProvider.rate_per_session
      : ''
  );
  const [ratePerEval, setRatePerEval] = useState<number | ''>(
    initialProvider?.rate_per_eval !== undefined &&
      initialProvider?.rate_per_eval !== null
      ? initialProvider.rate_per_eval
      : ''
  );
  const [payLadder, setPayLadder] = useState<'STANDARD' | 'CF_PLUS_5'>(
    initialProvider?.pay_ladder === 'CF_PLUS_5' ? 'CF_PLUS_5' : 'STANDARD'
  );
  const [active, setActive] = useState(
    initialProvider?.active !== undefined ? initialProvider.active === 1 : true
  );

  const [existingUsers, setExistingUsers] = useState<Array<{ id: string; email: string; name: string; role: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/users')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setExistingUsers(data))
      .catch(() => {});
  }, []);

  const matchedUser = useMemo(() => {
    if (initialProvider?.user_id) {
      const match = existingUsers.find((u) => u.id === initialProvider.user_id);
      if (match) return match;
    }
    if (initialProvider?.id === 'PROV-JUSTINEFREN') {
      const match = existingUsers.find((u) => u.id === 'USR-CREDENTIALING' || u.email?.toLowerCase() === 'credentialing@allegianttherapy.com' || u.id === 'USR-JUSTINEFREN');
      if (match) return match;
    }
    // For new providers, do NOT auto-infer by email! Clinical provider creation is login independent.
    return null;
  }, [existingUsers, initialProvider]);

  const formatUserAssignedRoles = (u: any) => {
    if (!u) return '';
    if (u.assignedRoles && u.assignedRoles.length > 0) {
      return u.assignedRoles.join(', ');
    }
    const clean = (u.email || '').toLowerCase();
    if (clean === 'vmedina@allegianttherapy.com' || clean === 'viviana@allegianttherapy.com') {
      return 'REFERRAL_COORDINATOR';
    }
    if (clean === 'credentialing@allegianttherapy.com' || clean === 'justinefren1985@gmail.com') {
      return 'ASSISTANT';
    }
    const r = u.role === 'ADMIN' ? 'OWNER' : u.role === 'OFFICE' ? 'REFERRAL_COORDINATOR' : u.role;
    return r;
  };

  // Auto-adjust default title when role changes if not custom edited
  const handleRoleChange = (newRole: 'SLP' | 'SLPA' | 'SUPERVISOR') => {
    setRole(newRole);
    if (!isEditing) {
      if (newRole === 'SLPA') {
        setTitle('Speech-Language Pathology Assistant');
        setRequiresSupervision(false);
      } else if (newRole === 'SUPERVISOR') {
        setTitle('Clinical Supervisor / Lead SLP');
        setRequiresSupervision(false);
        setSupervisorId('');
      } else {
        setTitle('Speech-Language Pathologist');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Therapist full name is required');
      return;
    }
    if ((role === 'SLPA' || requiresSupervision) && !supervisorId) {
      setError(role === 'SLPA'
        ? 'A designated Supervising SLP is required for an SLPA.'
        : 'A designated Supervising SLP is required when this SLP requires supervision.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        name: name.trim(),
        first_name: firstName.trim() || null,
        middle_name: middleName.trim() || null,
        last_name: lastName.trim() || null,
        suffix: suffix.trim() || null,
        credentials: ediCredentials.trim() || null,
        role,
        title: title.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        npi: npi.trim() || '',
        license_number: licenseNumber.trim() || '',
        ahcccs_id: ahcccsId.trim() || null,
        supervisor_id: (role === 'SLPA' || requiresSupervision) && supervisorId ? supervisorId : null,
        rate_per_session: ratePerSession !== '' ? Number(ratePerSession) : null,
        rate_per_eval: ratePerEval !== '' ? Number(ratePerEval) : null,
        pay_ladder: payLadder,
        requires_supervision: role === 'SLP' && requiresSupervision ? 1 : 0,
        active: active ? 1 : 0,
        user_id: initialProvider?.user_id || null
      };

      const url = isEditing ? `/api/providers/${initialProvider.id}` : '/api/providers';
      const method = isEditing ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const errorMsg = data.error || (res.status === 403 ? 'You do not have permission to modify providers.' : `Server returned status ${res.status}`);
        throw new Error(errorMsg);
      }

      onSaved();
    } catch (err: any) {
      console.error('[AddEditProviderModal] Save error:', err);
      setError(`Unable to ${isEditing ? 'update' : 'create'} therapist: ${err.message || 'An error occurred while saving.'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="glass-panel max-w-lg w-full rounded-2xl border border-slate-800 p-6 space-y-5 my-8">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center space-x-2">
            <UserCheck className="w-5 h-5 text-teal-700" />
            <h2 className="text-base font-bold text-slate-800">
              {isEditing ? `Edit Therapist: ${initialProvider.name}` : 'Add Therapist'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-300 rounded-lg text-xs flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div>
            <label className="block text-slate-700 font-semibold mb-1">
              Display Name *
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Jane Doe, MS, CCC-SLP"
              className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
            />
            <p className="mt-1 text-[10px] text-slate-500">Display name used throughout Bloom. It is saved separately from the legal EDI name.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Middle Name / Initial</label>
              <input
                type="text"
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
                placeholder="Optional"
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Suffix</label>
              <input
                type="text"
                value={suffix}
                onChange={(e) => setSuffix(e.target.value)}
                placeholder="Optional"
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-slate-700 font-semibold mb-1">Credentials</label>
              <input
                type="text"
                value={ediCredentials}
                onChange={(e) => setEdiCredentials(e.target.value)}
                placeholder="e.g. MS, CCC-SLP"
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Clinical Role *</label>
              <select
                value={role}
                onChange={(e) => handleRoleChange(e.target.value as any)}
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              >
                <option value="SLP">SLP (Speech-Language Pathologist)</option>
                <option value="SLPA">SLPA (Assistant)</option>
                <option value="SUPERVISOR">SUPERVISOR (Lead / Supervisor)</option>
              </select>
            </div>

            <div>
              <label className="block text-slate-700 font-semibold mb-1">Position / Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Speech-Language Pathologist"
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              />
            </div>
          </div>

          {role === 'SLP' && (
            <label className="flex items-start space-x-2 p-3 rounded-xl border border-slate-200 bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresSupervision}
                onChange={(e) => {
                  const next = e.target.checked;
                  setRequiresSupervision(next);
                  if (!next) setSupervisorId('');
                }}
                className="mt-0.5"
              />
              <span className="text-[11px] text-slate-700 leading-snug">
                <strong>Requires clinical supervision.</strong> Use for a CF SLP. Role and pay stay SLP. SOAP/QPR notes use the existing supervisor approval workflow.
              </span>
            </label>
          )}

          {/* Supervision Relationship (SLPA or supervised SLP) */}
          {(role === 'SLPA' || (role === 'SLP' && requiresSupervision)) && (
            <div className="p-3 bg-indigo-950/20 border border-indigo-800/40 rounded-xl space-y-1.5">
              <label className="block text-indigo-300 font-semibold flex items-center space-x-1.5">
                <ShieldCheck className="w-4 h-4 text-indigo-400" />
                <span>Designated Supervising SLP {role === 'SLPA' ? '(Required for SLPAs)' : '(Required for supervised SLP)'}</span>
              </label>
              <select
                value={supervisorId}
                onChange={(e) => setSupervisorId(e.target.value)}
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              >
                <option value="">-- Select Supervising SLP --</option>
                {activeSupervisors.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.role})
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-slate-400">
                {role === 'SLPA'
                  ? 'All clinical notes authored by this SLPA will automatically route to the selected supervisor for co-signature.'
                  : 'SOAP and QPR notes authored by this SLP will route to the selected supervisor for co-signature. Evaluations and POCs stay SLP-authored.'}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="provider-edit-email-input" className="block text-slate-700 font-semibold mb-1">
                Clinical Contact / eSignature Email
              </label>
              <input
                id="provider-edit-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. clinician@allegianttherapy.com or personal email"
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Used for clinical contact and SignWell treating-provider routing. Independent from App User login email.
              </p>
            </div>

            <div>
              <label htmlFor="provider-edit-phone-input" className="block text-slate-700 font-semibold mb-1">Phone Number</label>
              <input
                id="provider-edit-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(XXX) XXX-XXXX"
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              />
            </div>
          </div>

          {/* Canonical User Identity Linking Status */}
          {matchedUser ? (
            <div className="p-2.5 rounded-xl bg-emerald-950/30 border border-emerald-800/50 text-emerald-300 text-[11px] flex items-center space-x-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>
                <strong>Linked App User (Login / RBAC):</strong> {matchedUser.name} ({matchedUser.email}) — Roles: {formatUserAssignedRoles(matchedUser)}
              </span>
            </div>
          ) : (
            <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/90 text-slate-600 text-[11px] flex items-start space-x-2">
              <UserCheck className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
              <span>
                <strong>Provider-Record Only:</strong> App login access is not linked. Clinical profile is active for caseload assignment, scheduling, documentation, and billing.
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">NPI Number (10 digits)</label>
              <input
                type="text"
                value={npi}
                onChange={(e) => setNpi(e.target.value)}
                placeholder="1892837461"
                maxLength={10}
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 font-mono placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              />
            </div>

            <div>
              <label className="block text-slate-700 font-semibold mb-1">State License #</label>
              <input
                type="text"
                value={licenseNumber}
                onChange={(e) => setLicenseNumber(e.target.value)}
                placeholder="SLP-AZ-382910"
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 font-mono placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              />
            </div>

            <div>
              <label className="block text-slate-700 font-semibold mb-1">Individual AHCCCS ID</label>
              <input
                type="text"
                value={ahcccsId}
                onChange={(e) => setAhcccsId(e.target.value)}
                placeholder="e.g. 123456"
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 font-mono placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
              />
              <p className="text-[10px] text-slate-400 mt-1">Required for DDD rendering provider claims</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Treatment Pay Ladder</label>
              <select
                value={payLadder}
                onChange={(e) => setPayLadder(e.target.value === 'CF_PLUS_5' ? 'CF_PLUS_5' : 'STANDARD')}
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300 text-xs"
              >
                <option value="STANDARD">STANDARD — existing weekly ladder</option>
                <option value="CF_PLUS_5">CF_PLUS_5 — same bands, +$5 each tier</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Rate Per Session ($)</label>
              <input
                type="number"
                step="0.01"
                value={ratePerSession}
                onChange={(e) => setRatePerSession(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Leave empty to use selected ladder"
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 font-mono placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300 text-xs"
              />
            </div>

            <div>
              <label className="block text-slate-700 font-semibold mb-1">Rate Per Eval ($)</label>
              <input
                type="number"
                step="0.01"
                value={ratePerEval}
                onChange={(e) => setRatePerEval(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Standard ($150 Tele / $175 Onsite)"
                className="w-full bg-white border border-slate-200/90 rounded-lg px-3 py-2 text-slate-800 font-mono placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300 text-xs"
              />
            </div>
          </div>

          <div className="pt-2 flex items-center justify-between border-t border-slate-200">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="rounded border-slate-300 bg-white text-slate-800 focus:ring-1 focus:ring-slate-300 w-4 h-4"
              />
              <span className="text-slate-700 font-medium">Active Therapist Status</span>
            </label>

            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-1.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors shadow-sm"
              >
                {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Therapist'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// CASELOAD MANAGER MODAL (BULK & INDIVIDUAL ASSIGNMENT)
// ----------------------------------------------------------------------
interface CaseloadManagerModalProps {
  provider: Provider;
  allProviders: Provider[];
  allClients: Client[];
  isAdmin: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

const CaseloadManagerModal: React.FC<CaseloadManagerModalProps> = ({
  provider,
  allProviders,
  allClients,
  isAdmin,
  onClose,
  onUpdated
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'CURRENT' | 'ASSIGN_BULK' | 'TRANSFER'>('CURRENT');
  const [assignedClients, setAssignedClients] = useState<any[]>([]);
  const [loadingCaseload, setLoadingCaseload] = useState(true);

  // Bulk Assign State
  const [searchAvailable, setSearchAvailable] = useState('');
  const [selectedClientIdsToAssign, setSelectedClientIdsToAssign] = useState<string[]>([]);
  const [bulkFrequency, setBulkFrequency] = useState('2x/week');
  const [bulkDuration, setBulkDuration] = useState('60');
  const [submittingBulk, setSubmittingBulk] = useState(false);

  // Transfer State
  const [selectedClientIdsToTransfer, setSelectedClientIdsToTransfer] = useState<string[]>([]);
  const [targetProviderId, setTargetProviderId] = useState('');
  const [submittingTransfer, setSubmittingTransfer] = useState(false);

  // Fetch current provider assignments
  const fetchProviderAssignments = async () => {
    try {
      setLoadingCaseload(true);
      const res = await fetch(`/api/assignments?providerId=${provider.id}&activeOnly=1`);
      if (res.ok) {
        const data = await res.json();
        setAssignedClients(data);
      }
    } catch (err) {
      console.error('Error fetching provider assignments:', err);
    } finally {
      setLoadingCaseload(false);
    }
  };

  useEffect(() => {
    fetchProviderAssignments();
  }, [provider.id]);

  // Available clients: active clients NOT currently assigned to this provider
  const assignedClientIdsSet = useMemo(() => {
    return new Set(assignedClients.map((ac) => ac.client_id));
  }, [assignedClients]);

  const availableClients = useMemo(() => {
    return allClients.filter((c) => {
      if (assignedClientIdsSet.has(c.id)) return false;
      if (searchAvailable.trim()) {
        const q = searchAvailable.toLowerCase();
        const matchesName = `${c.first_name} ${c.last_name}`.toLowerCase().includes(q);
        const matchesId = c.id?.toLowerCase().includes(q);
        const matchesAhcccs = c.ahcccs_id?.toLowerCase().includes(q);
        const matchesPayer = c.primary_payer_name?.toLowerCase().includes(q);
        if (!matchesName && !matchesId && !matchesAhcccs && !matchesPayer) return false;
      }
      return true;
    });
  }, [allClients, assignedClientIdsSet, searchAvailable]);

  // Bulk Assign Handler
  const handleBulkAssign = async () => {
    if (selectedClientIdsToAssign.length === 0) return;
    try {
      setSubmittingBulk(true);
      const res = await fetch(`/api/providers/${provider.id}/assignments/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_ids: selectedClientIdsToAssign,
          expected_frequency: bulkFrequency,
          expected_duration_minutes: Number(bulkDuration)
        })
      });
      if (res.ok) {
        setSelectedClientIdsToAssign([]);
        await fetchProviderAssignments();
        onUpdated();
        setActiveSubTab('CURRENT');
      }
    } catch (err) {
      console.error('Bulk assign failed:', err);
    } finally {
      setSubmittingBulk(false);
    }
  };

  // Transfer Handler
  const handleBulkTransfer = async () => {
    if (selectedClientIdsToTransfer.length === 0 || !targetProviderId) return;
    try {
      setSubmittingTransfer(true);
      const res = await fetch('/api/assignments/reassign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_ids: selectedClientIdsToTransfer,
          to_provider_id: targetProviderId
        })
      });
      if (res.ok) {
        setSelectedClientIdsToTransfer([]);
        await fetchProviderAssignments();
        onUpdated();
        setActiveSubTab('CURRENT');
      }
    } catch (err) {
      console.error('Transfer failed:', err);
    } finally {
      setSubmittingTransfer(false);
    }
  };

  // Single unassign handler
  const handleUnassignSingle = async (clientId: string) => {
    if (!confirm('Remove this client from provider caseload?')) return;
    try {
      const res = await fetch(`/api/providers/${provider.id}/assignments/${clientId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        await fetchProviderAssignments();
        onUpdated();
      }
    } catch (err) {
      console.error('Unassign failed:', err);
    }
  };

  // Checkbox toggle helpers
  const toggleSelectToAssign = (clientId: string) => {
    setSelectedClientIdsToAssign((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    );
  };

  const toggleSelectAllAvailable = () => {
    if (selectedClientIdsToAssign.length === availableClients.length) {
      setSelectedClientIdsToAssign([]);
    } else {
      setSelectedClientIdsToAssign(availableClients.map((c) => c.id));
    }
  };

  const toggleSelectToTransfer = (clientId: string) => {
    setSelectedClientIdsToTransfer((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white max-w-4xl w-full rounded-2xl border border-slate-200/90 shadow-xl overflow-hidden my-8 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 bg-white border-b border-slate-100 flex justify-between items-center">
          <div>
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-sky-700" />
              <h2 className="text-base font-bold text-slate-900">Caseload Management: {provider.name}</h2>
              <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200">
                {provider.role}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {assignedClients.length} clients currently assigned to this therapist.
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Sub-Tabs */}
        <div className="flex border-b border-slate-100 bg-slate-50 px-5 pt-2 text-xs">
          <button
            onClick={() => setActiveSubTab('CURRENT')}
            className={`pb-2.5 px-4 font-semibold border-b-2 transition-colors ${
              activeSubTab === 'CURRENT'
                ? 'border-sky-600 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100/80'
            }`}
          >
            Assigned Clients ({assignedClients.length})
          </button>

          {isAdmin && (
            <>
              <button
                onClick={() => setActiveSubTab('ASSIGN_BULK')}
                className={`pb-2.5 px-4 font-semibold border-b-2 transition-colors flex items-center space-x-1.5 ${
                  activeSubTab === 'ASSIGN_BULK'
                    ? 'border-sky-600 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100/80'
                }`}
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Bulk / Individual Assign</span>
              </button>

              <button
                onClick={() => setActiveSubTab('TRANSFER')}
                className={`pb-2.5 px-4 font-semibold border-b-2 transition-colors flex items-center space-x-1.5 ${
                  activeSubTab === 'TRANSFER'
                    ? 'border-sky-600 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100/80'
                }`}
              >
                <ArrowRightLeft className="w-3.5 h-3.5" />
                <span>Reassign / Transfer Caseload</span>
              </button>
            </>
          )}
        </div>

        {/* Content Area */}
        <div className="p-5 overflow-y-auto flex-1 text-xs">
          {/* TAB 1: CURRENT CASELOAD */}
          {activeSubTab === 'CURRENT' && (
            <div className="space-y-4">
              {loadingCaseload ? (
                <div className="p-8 text-center text-slate-500">Loading assignments...</div>
              ) : assignedClients.length === 0 ? (
                <div className="p-8 text-center space-y-3">
                  <p className="text-slate-500">No clients currently assigned to this provider.</p>
                  {isAdmin && (
                    <button
                      onClick={() => setActiveSubTab('ASSIGN_BULK')}
                      className="px-3.5 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-bold"
                    >
                      Assign Clients from Roster
                    </button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden bg-white">
                  {assignedClients.map((c) => (
                    <div
                      key={c.id}
                      className="p-3.5 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    >
                      <div>
                        <div className="font-semibold text-slate-800 text-sm">
                          {c.client_last_name}, {c.client_first_name}
                        </div>
                        <div className="text-[11px] text-slate-500 font-mono mt-0.5 space-x-2">
                          <span>AHCCCS: {c.ahcccs_id || 'N/A'}</span>
                          <span>•</span>
                          <span>DOB: {c.client_dob || 'N/A'}</span>
                          <span>•</span>
                          <span className="text-sky-800 font-medium">Freq: {c.expected_frequency || c.auth_frequency || '2x/week'}</span>
                          <span>•</span>
                          <span>Assigned: {c.assigned_date}</span>
                        </div>
                        {c.supervisor_name && (
                          <div className="text-[10px] text-indigo-800 mt-1 flex items-center space-x-1">
                            <ShieldCheck className="w-3 h-3" />
                            <span>Supervised by {c.supervisor_name}</span>
                          </div>
                        )}
                      </div>

                      {isAdmin && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleUnassignSingle(c.client_id)}
                            className="p-1.5 text-rose-600 hover:text-rose-700 rounded hover:bg-rose-50 transition-colors"
                            title="Unassign from provider"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 2: BULK ASSIGN */}
          {activeSubTab === 'ASSIGN_BULK' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
                <div className="relative w-full sm:w-72">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search available clients..."
                    value={searchAvailable}
                    onChange={(e) => setSearchAvailable(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  />
                </div>

                <div className="flex items-center space-x-2 w-full sm:w-auto justify-end">
                  <div className="flex items-center space-x-1 text-slate-700">
                    <span className="text-[11px] text-slate-600">Freq:</span>
                    <select
                      value={bulkFrequency}
                      onChange={(e) => setBulkFrequency(e.target.value)}
                      className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs text-slate-800"
                    >
                      <option value="1x/week">1x/week</option>
                      <option value="2x/week">2x/week</option>
                      <option value="3x/week">3x/week</option>
                      <option value="4x/month">4x/month</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-1 text-slate-700">
                    <span className="text-[11px] text-slate-600">Dur:</span>
                    <select
                      value={bulkDuration}
                      onChange={(e) => setBulkDuration(e.target.value)}
                      className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs text-slate-800"
                    >
                      <option value="30">30 min</option>
                      <option value="45">45 min</option>
                      <option value="60">60 min</option>
                    </select>
                  </div>

                  <button
                    onClick={handleBulkAssign}
                    disabled={selectedClientIdsToAssign.length === 0 || submittingBulk}
                    className="px-3 py-1.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white font-bold rounded-lg transition-colors flex items-center space-x-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Assign ({selectedClientIdsToAssign.length})</span>
                  </button>
                </div>
              </div>

              {/* Table of Available Clients */}
              <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
                <div className="p-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs text-slate-600">
                  <label className="flex items-center space-x-2 cursor-pointer font-semibold">
                    <input
                      type="checkbox"
                      checked={
                        availableClients.length > 0 &&
                        selectedClientIdsToAssign.length === availableClients.length
                      }
                      onChange={toggleSelectAllAvailable}
                      className="rounded border-slate-300 bg-white text-sky-600 focus:ring-2 focus:ring-slate-400 w-3.5 h-3.5"
                    />
                    <span>Select All Available ({availableClients.length})</span>
                  </label>
                  <span>{selectedClientIdsToAssign.length} selected</span>
                </div>

                <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                  {availableClients.length === 0 ? (
                    <div className="p-6 text-center text-slate-500">
                      No additional clients available to assign.
                    </div>
                  ) : (
                    availableClients.map((c) => {
                      const isSelected = selectedClientIdsToAssign.includes(c.id);
                      return (
                        <div
                          key={c.id}
                          onClick={() => toggleSelectToAssign(c.id)}
                          className={`p-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors ${
                            isSelected ? 'bg-sky-50' : ''
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              className="rounded border-slate-300 bg-white text-sky-600 focus:ring-2 focus:ring-slate-400 w-3.5 h-3.5"
                            />
                            <div>
                              <div className="font-semibold text-slate-800">
                                {c.last_name}, {c.first_name}
                              </div>
                              <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                                ID: {c.id} • AHCCCS: {c.ahcccs_id || 'N/A'} • Payer: {c.primary_payer_name} • City: {c.city || 'N/A'}
                              </div>
                            </div>
                          </div>

                          <div className="text-[11px] font-mono text-slate-500">
                            {c.poc_status ? `POC: ${c.poc_status}` : 'Pending POC'}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: REASSIGN / TRANSFER */}
          {activeSubTab === 'TRANSFER' && (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
                <div className="text-amber-800 font-bold flex items-center space-x-1.5">
                  <ArrowRightLeft className="w-4 h-4" />
                  <span>Transfer Caseload to Another Active Provider</span>
                </div>
                <p className="text-xs text-slate-700 leading-relaxed">
                  Select clients currently on {provider.name}'s caseload and transfer them in bulk
                  to another provider. The system will cleanly close the prior assignment and
                  establish active assignments with the recipient provider.
                </p>
              </div>

              <div className="flex items-center space-x-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
                <label className="text-slate-700 font-semibold text-xs whitespace-nowrap">
                  Recipient Provider:
                </label>
                <select
                  value={targetProviderId}
                  onChange={(e) => setTargetProviderId(e.target.value)}
                  className="flex-1 bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">-- Select Recipient Therapist --</option>
                  {allProviders.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.role} • {p.caseload_count || 0} clients)
                    </option>
                  ))}
                </select>

                <button
                  onClick={handleBulkTransfer}
                  disabled={
                    selectedClientIdsToTransfer.length === 0 || !targetProviderId || submittingTransfer
                  }
                  className="px-4 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white font-bold rounded-lg transition-colors flex items-center space-x-1 whitespace-nowrap"
                >
                  <ArrowRightLeft className="w-3.5 h-3.5" />
                  <span>Transfer ({selectedClientIdsToTransfer.length}) Clients</span>
                </button>
              </div>

              {/* Client selection for transfer */}
              <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
                <div className="p-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs text-slate-600">
                  <label className="flex items-center space-x-2 cursor-pointer font-semibold">
                    <input
                      type="checkbox"
                      checked={
                        assignedClients.length > 0 &&
                        selectedClientIdsToTransfer.length === assignedClients.length
                      }
                      onChange={() => {
                        if (selectedClientIdsToTransfer.length === assignedClients.length) {
                          setSelectedClientIdsToTransfer([]);
                        } else {
                          setSelectedClientIdsToTransfer(assignedClients.map((c) => c.client_id));
                        }
                      }}
                      className="rounded border-slate-300 bg-white text-amber-600 focus:ring-2 focus:ring-slate-400 w-3.5 h-3.5"
                    />
                    <span>Select All Assigned Clients</span>
                  </label>
                  <span>{selectedClientIdsToTransfer.length} selected</span>
                </div>

                <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                  {assignedClients.map((c) => {
                    const isSelected = selectedClientIdsToTransfer.includes(c.client_id);
                    return (
                      <div
                        key={c.id}
                        onClick={() => toggleSelectToTransfer(c.client_id)}
                        className={`p-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors ${
                          isSelected ? 'bg-amber-50' : ''
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="rounded border-slate-300 bg-white text-amber-600 focus:ring-2 focus:ring-slate-400 w-3.5 h-3.5"
                          />
                          <div>
                            <div className="font-semibold text-slate-800">
                              {c.client_last_name}, {c.client_first_name}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                              AHCCCS: {c.ahcccs_id || 'N/A'} • Assigned: {c.assigned_date}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
